import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { Moment } from 'moment-timezone';
import { formatDuration, useFridgeTempHistory } from '../hvac/useFridgeTempHistory';

interface FridgeTempStatsProps {
  deviceEUI: string;
  startDateTime: Moment;
  endDateTime: Moment;
  // Upper bound of the acceptable (green) temperature band. A "spike" is any
  // contiguous run where TempF crosses above this value; recovery is the time
  // from that spike's peak back down to <= nominalMax.
  nominalMax: number;
  unit?: string;
  // Latest live TempF, merged onto history so stats track websocket updates.
  liveTempF?: number | null;
}

const StatRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', px: 1 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
      {value}
    </Typography>
  </Box>
);

const FridgeTempStats: React.FC<FridgeTempStatsProps> = ({
  deviceEUI,
  startDateTime,
  endDateTime,
  nominalMax,
  unit = '°F',
  liveTempF,
}) => {
  const { stats, isLoading, isError } = useFridgeTempHistory(
    deviceEUI,
    startDateTime,
    endDateTime,
    nominalMax,
    liveTempF,
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, px: 1, py: 0.5 }}>
        <Skeleton height={18} />
        <Skeleton height={18} />
        <Skeleton height={18} />
      </Box>
    );
  }

  if (isError || !stats) {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary', px: 1 }}>
        Stats unavailable
      </Typography>
    );
  }

  if (stats.count === 0) {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary', px: 1 }}>
        No readings in selected range
      </Typography>
    );
  }

  const fmtTemp = (v: number | null) =>
    v === null ? '—' : `${v.toFixed(1)}${unit}`;

  const recoveryText =
    stats.spikes === 0
      ? '—'
      : `${formatDuration(stats.minRecoverySec)} – ${formatDuration(stats.maxRecoverySec)}`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <StatRow label="Min / Max" value={`${fmtTemp(stats.min)} / ${fmtTemp(stats.max)}`} />
      <StatRow label="Spikes" value={String(stats.spikes)} />
      <StatRow label="Recovery" value={recoveryText} />
    </Box>
  );
};

export default FridgeTempStats;
